import React, { useId, useRef } from 'react'
import { Building2, Check, Layers } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { HotelAccess, HotelAccessDraft, HotelAccessScope, HotelOption, HotelPicker } from '@/types/team'

// WHICH HOTELS A MANAGER OR VIEWER CAN OPEN (change safety R4.4, R4.5).
//
// Two choices, then a checklist. "All hotels" is the default and what every teammate had before
// this existed; "Only these hotels" opens the list of the business's hotels. A combined inventory is
// one row under its lead listing's name: ticking it gives every listing, because it is given whole.
//
// The picker holds the choice and nothing else. Every sentence on it arrives from the server
// (`HotelPicker`), and what it sends back is the ids of the ticked options, exactly as the server
// listed them. Shared by the invite form and HotelAccessDialog, so the two can never offer a
// different choice.
//
// KEYBOARD. The two choices are a radio group: Tab lands on the chosen one, the arrow keys move
// between them. Each hotel is a real checkbox (visually replaced, still focusable), so Space ticks
// it and a screen reader reads its label and its combined inventory line.

/** The draft a row's current access opens with. A combined inventory counts as chosen only whole. */
export function draftFromAccess(access: HotelAccess | null, options: HotelOption[]): HotelAccessDraft {
  if (!access || access.scope === 'all' || !access.propertyIds) return { scope: 'all', selected: [] }

  const held = new Set(access.propertyIds)
  return {
    scope: 'some',
    selected: options.filter((o) => o.propertyIds.every((id) => held.has(id))).map((o) => o.value),
  }
}

/** The ids to send for a draft: null for every hotel, else every listing of every ticked option. */
export function propertyIdsFor(draft: HotelAccessDraft, options: HotelOption[]): number[] | null {
  if (draft.scope === 'all') return null

  const chosen = new Set(draft.selected)
  return options.filter((o) => chosen.has(o.value)).flatMap((o) => o.propertyIds)
}

/** True when the draft cannot be saved: "Only these hotels" with nothing ticked. */
export function draftIsEmpty(draft: HotelAccessDraft): boolean {
  return draft.scope === 'some' && draft.selected.length === 0
}

interface HotelAccessPickerProps {
  picker: HotelPicker
  draft: HotelAccessDraft
  onChange: (draft: HotelAccessDraft) => void
  disabled?: boolean
  /** Caps the checklist's height where the picker sits inside a page rather than a dialog. */
  listClassName?: string
}

const SCOPES: { value: HotelAccessScope; label: string }[] = [
  { value: 'all', label: 'All hotels' },
  { value: 'some', label: 'Only these hotels' },
]

export default function HotelAccessPicker({ picker, draft, onChange, disabled, listClassName }: HotelAccessPickerProps) {
  const groupId = useId()
  const radioRefs = useRef<(HTMLButtonElement | null)[]>([])
  const describe = (scope: HotelAccessScope) => (scope === 'all' ? picker.allDescription : picker.someDescription)

  function choose(scope: HotelAccessScope) {
    if (disabled || scope === draft.scope) return
    onChange({ scope, selected: draft.selected })
  }

  function onRadioKeyDown(event: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp']
    if (!keys.includes(event.key)) return
    event.preventDefault()
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1
    const next = (index + step + SCOPES.length) % SCOPES.length
    choose(SCOPES[next].value)
    radioRefs.current[next]?.focus()
  }

  function toggle(value: string) {
    if (disabled) return
    const selected = draft.selected.includes(value)
      ? draft.selected.filter((v) => v !== value)
      : [...draft.selected, value]
    onChange({ scope: 'some', selected })
  }

  const showList = draft.scope === 'some'

  return (
    <div className="space-y-3">
      <div role="radiogroup" aria-label="Hotel access" className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {SCOPES.map((scope, index) => {
          const checked = draft.scope === scope.value
          const descriptionId = `${groupId}-${scope.value}-description`
          return (
            <button
              key={scope.value}
              ref={(el) => {
                radioRefs.current[index] = el
              }}
              type="button"
              role="radio"
              aria-checked={checked}
              aria-describedby={descriptionId}
              tabIndex={checked ? 0 : -1}
              disabled={disabled}
              onClick={() => choose(scope.value)}
              onKeyDown={(event) => onRadioKeyDown(event, index)}
              className={cn(
                'group flex cursor-pointer items-start gap-2.5 rounded-lg border bg-white px-3.5 py-3 text-left transition-colors duration-150',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1',
                'disabled:cursor-not-allowed disabled:opacity-60',
                checked
                  ? 'border-primary/60 bg-primary/[0.04] shadow-sm'
                  : 'border-border/60 hover:border-border hover:bg-muted/40'
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors duration-150',
                  checked ? 'border-primary bg-primary' : 'border-slate-300 bg-white group-hover:border-slate-400'
                )}
              >
                <span className={cn('h-1.5 w-1.5 rounded-full bg-white transition-transform duration-150', checked ? 'scale-100' : 'scale-0')} />
              </span>
              <span className="min-w-0">
                <span className={cn('block text-sm font-medium', checked ? 'text-foreground' : 'text-foreground/90')}>
                  {scope.label}
                </span>
                <span id={descriptionId} className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
                  {describe(scope.value)}
                </span>
              </span>
            </button>
          )
        })}
      </div>

      {/* The checklist folds open under "Only these hotels" (grid rows 0fr to 1fr, so it grows to its
          real height with no measuring), and is inert while folded so Tab never lands in it. */}
      <div
        className={cn(
          'grid transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none',
          showList ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
        inert={!showList}
        data-testid="hotel-checklist"
        data-open={showList ? 'true' : 'false'}
      >
        <div className="min-h-0 overflow-hidden">
          {picker.options.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm">
              <Building2 className="mx-auto h-8 w-8 text-muted-foreground/40" />
              <p className="mt-3 text-sm font-medium text-foreground">No hotels yet</p>
              <p className="mt-1 text-xs text-muted-foreground">Add a hotel to this business to choose it here.</p>
            </div>
          ) : (
            // `min-w-0` because a fieldset's default minimum width is its content's: without it a long
            // hotel name widens the list past a phone-width dialog and the right edge is cut off.
            <fieldset className="min-w-0 overflow-hidden rounded-lg border border-border/60 bg-white" data-testid="hotel-fieldset">
              <legend className="sr-only">Hotels</legend>
              <div className="flex items-center justify-between border-b border-border/40 bg-muted/30 px-3.5 py-2">
                <span className="text-xs font-medium text-muted-foreground">Hotels</span>
                <span className="text-xs tabular-nums text-muted-foreground" data-testid="hotel-chosen-count">
                  {draft.selected.length} of {picker.options.length} chosen
                </span>
              </div>
              <div className={cn('divide-y divide-border/50 overflow-y-auto', listClassName)}>
                {picker.options.map((option) => {
                  const checked = draft.selected.includes(option.value)
                  const combined = option.sublabel !== null
                  const inputId = `${groupId}-${option.value}`
                  return (
                    <label
                      key={option.value}
                      htmlFor={inputId}
                      className={cn(
                        'flex cursor-pointer items-start gap-3 px-3.5 py-2.5 transition-colors duration-150',
                        checked ? 'bg-primary/[0.04]' : 'hover:bg-muted/40',
                        disabled && 'cursor-not-allowed opacity-60'
                      )}
                    >
                      <input
                        id={inputId}
                        type="checkbox"
                        className="peer sr-only"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => toggle(option.value)}
                        aria-describedby={combined ? `${inputId}-sublabel` : undefined}
                      />
                      <span
                        aria-hidden="true"
                        className={cn(
                          'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors duration-150',
                          'peer-focus-visible:ring-2 peer-focus-visible:ring-primary/40 peer-focus-visible:ring-offset-1',
                          checked ? 'border-primary bg-primary text-white' : 'border-slate-300 bg-white'
                        )}
                      >
                        <Check className={cn('h-3 w-3 transition-opacity duration-150', checked ? 'opacity-100' : 'opacity-0')} strokeWidth={3} />
                      </span>
                      <span className="min-w-0 flex-1">
                        {/* The whole name, wrapped: hotels are chosen by name, and a cut-off name can
                            hide the words that tell two listings apart. */}
                        <span className="flex items-start gap-1.5">
                          {combined ? (
                            <Layers className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary/80" aria-hidden="true" />
                          ) : (
                            <Building2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground/70" aria-hidden="true" />
                          )}
                          <span className="min-w-0 break-words text-sm font-medium text-foreground">{option.label}</span>
                        </span>
                        {/* One line each, as the server sent them: what it is, then its following
                            listings, so names that contain "and" never run together. */}
                        {combined && (
                          <span id={`${inputId}-sublabel`} className="mt-0.5 block space-y-0.5 pl-5 text-xs leading-relaxed text-muted-foreground">
                            {option.sublabel?.map((line) => (
                              <span key={line} className="block break-words">
                                {line}
                              </span>
                            ))}
                          </span>
                        )}
                      </span>
                    </label>
                  )
                })}
              </div>
            </fieldset>
          )}
          {draftIsEmpty(draft) && picker.options.length > 0 && (
            <p className="mt-2 text-xs text-amber-700" role="status">
              {picker.emptyMessage}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
